const SHARE_SITE_URL = 'https://www.trampolinehealth.co/';

function measureShareLines(ctx, blocks, maxWidth, lineHeight = 64, paragraphGap = 36) {
    const fallback = blocks.length ? blocks : ['Trampoline Health'];
    const allLines = [];

    fallback.forEach((block, blockIndex) => {
        if (blockIndex > 0) allLines.push({ text: '', gap: true });
        const paragraphs = String(block).split(/\n+/);
        paragraphs.forEach((paragraph, paragraphIndex) => {
            if (paragraphIndex > 0) allLines.push({ text: '' });
            const words = paragraph.trim().split(/\s+/).filter(Boolean);
            let current = '';
            words.forEach((word) => {
                const test = current ? `${current} ${word}` : word;
                if (ctx.measureText(test).width > maxWidth && current) {
                    allLines.push({ text: current });
                    current = word;
                } else {
                    current = test;
                }
            });
            if (current) allLines.push({ text: current });
        });
    });

    const contentHeight = allLines.reduce((sum, line) => sum + (line.gap ? paragraphGap : lineHeight), 0);
    return { allLines, contentHeight, lineHeight, paragraphGap };
}

function drawShareText(ctx, measured, x, startY) {
    const { allLines, lineHeight, paragraphGap } = measured;
    let cursorY = startY + lineHeight / 2;

    allLines.forEach((line) => {
        if (line.gap) {
            cursorY += paragraphGap;
            return;
        }
        if (line.text) ctx.fillText(line.text, x, cursorY);
        cursorY += lineHeight;
    });
}

export function downloadBlob(blob, filename) {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
}

function canShareFiles(file) {
    try {
        return !!(navigator.share && navigator.canShare && navigator.canShare({ files: [file] }));
    } catch {
        return false;
    }
}

function canShareText() {
    return typeof navigator.share === 'function';
}

function buildShareCaption(text) {
    const highlight = String(text || '').trim() || 'Practicing emotional fitness with Trampoline Health.';
    return `${highlight}\n\n${SHARE_SITE_URL}`;
}

/**
 * Build a 1080×1350 PNG with centered takeaway text and Trampoline logo (Events-style).
 */
export async function createShareProgressImage(logoSrc, takeawayText) {
    const blocks = [String(takeawayText || '').trim()].filter(Boolean);
    if (!blocks.length) blocks.push('My takeaway from Trampoline Health');

    const canvas = document.createElement('canvas');
    canvas.width = 1080;
    canvas.height = 1350;
    const ctx = canvas.getContext('2d');
    const gradient = ctx.createRadialGradient(220, 240, 40, 540, 675, 900);
    gradient.addColorStop(0, '#111F16');
    gradient.addColorStop(1, '#0A110E');
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    const logo = new Image();
    logo.src = logoSrc;
    await logo.decode();
    const maxW = 560;
    const maxH = 140;
    const scale = Math.min(maxW / logo.width, maxH / logo.height);
    const w = logo.width * scale;
    const h = logo.height * scale;

    ctx.fillStyle = '#FFFFFF';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.font = "300 54px Fraunces, serif";
    const measured = measureShareLines(ctx, blocks, 900, 68, 40);
    const logoTextGap = 88;
    const groupHeight = measured.contentHeight + logoTextGap + h;
    const groupTop = Math.max(40, (1350 - groupHeight) / 2);
    drawShareText(ctx, measured, 540, groupTop);
    ctx.drawImage(logo, (1080 - w) / 2, groupTop + measured.contentHeight + logoTextGap, w, h);

    const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));
    if (!blob) throw new Error('Could not create image');
    const file = new File([blob], 'trampoline-health.png', { type: 'image/png' });
    const caption = buildShareCaption(blocks[0]);
    return { blob, file, caption };
}

/** Native share sheet or download fallback (same behavior as Events). */
export async function shareOrDownloadProgressImage(logoSrc, takeawayText) {
    const { blob, file, caption } = await createShareProgressImage(logoSrc, takeawayText);
    const shareWithFiles = {
        files: [file],
        title: 'Trampoline Health',
        text: caption,
    };
    const shareTextOnly = {
        title: 'Trampoline Health',
        text: caption,
        url: SHARE_SITE_URL,
    };

    if (canShareFiles(file)) {
        await navigator.share(shareWithFiles);
        return { mode: 'share-files' };
    }
    if (canShareText()) {
        await navigator.share(shareTextOnly);
        downloadBlob(blob, 'trampoline-health.png');
        return { mode: 'share-text-and-download' };
    }
    downloadBlob(blob, 'trampoline-health.png');
    return { mode: 'download' };
}
